/** @format */

import {useCallback, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {ExecutiveReport} from 'ui';
import {ROUTES} from 'constants/ROUTES';
import {useSearchParams} from 'helpers/routerHelpers';

import {ERROR_REASON_LABEL, STATUS_LABEL, type ReportErrorReason} from '../content';
import {
  useCreateProjectReportPublicLinkMutation,
  useDeleteProjectReportPublicLinkMutation,
  useProjectReportSnapshotQuery,
} from '../graphql/__generated__';
import {ReportUnavailable} from './components/ReportUnavailable';
import {apolloErrorMessage} from './helpers/apolloErrorMessage';
import {toDataDate} from './helpers/toDataDate';
import {toReportData} from './helpers/toReportData';

const NBSP = '\u00a0';

/**
 * Один управленческий отчёт.
 *
 * Идентификатор приходит GET-параметром: ссылку на отчёт пересылают, и
 * адрес должен читаться без знания внутренней структуры маршрутов.
 *
 * Состояния сборки разбирает контейнер, а не сам документ: страница из
 * дизайн-системы печатает готовый снимок и про стадии бэкенда не знает.
 */
const Report = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const id = searchParams.get('id');

  const {data, loading, error, refetch} = useProjectReportSnapshotQuery({
    variables: {id: id ?? ''},
    skip: !id,
  });

  const report = data?.currentUser?.projectReport;
  const snapshot = report?.reportData;

  // Снимок обходит целиком типограф (глубокий рекурсивный обход), поэтому
  // приведение считаем один раз на снимок, а не на каждый рендер страницы —
  // тот же приём, о котором предупреждает `mapChartsData` в Digital Cast.
  const reportData = useMemo(() => (snapshot ? toReportData(snapshot) : null), [snapshot]);

  const handleLogoClick = useCallback(() => {
    navigate(`/${ROUTES.REPORTS}`);
  }, [navigate]);

  const [createPublicLink] = useCreateProjectReportPublicLinkMutation();
  const [deletePublicLink] = useDeleteProjectReportPublicLinkMutation();

  // Гард от повторного нажатия синхронный: `loading` встаёт на рендер позже
  // самого вызова, а удаление ссылки необратимо.
  const linkBusyRef = useRef(false);

  /*
   * Видимая занятость идёт рядом с гардом, а не из `loading` мутаций.
   *
   * `loading` падает в `false`, как только мутация ответила, а гард держится
   * до конца `refetch()`. В этом окне кнопки панели выглядели живыми, но
   * клики уходили в ранний `return` — ни запроса, ни спиннера, ни ошибки, —
   * и всё это поверх карточки, показывающей уже отозванный адрес.
   */
  const [linkBusy, setLinkBusy] = useState(false);

  // Ошибка последней попытки создать/отозвать ссылку. Молчание здесь опасно
  // в обе стороны: для необратимого удаления человек должен точно знать,
  // что оно не состоялось, а не гадать по неизменившейся панели.
  const [publicLinkError, setPublicLinkError] = useState<string | null>(null);

  const handleCreatePublicLink = useCallback(async () => {
    if (!id || linkBusyRef.current) return;
    linkBusyRef.current = true;
    setLinkBusy(true);
    setPublicLinkError(null);
    try {
      await createPublicLink({variables: {reportId: id}});
      // Никаких `refetchQueries`: клиент живёт на `no-cache`, обновляемся
      // тем же хуком, что читал снимок.
      await refetch();
    } catch (cause) {
      setPublicLinkError(
        apolloErrorMessage(
          cause,
          t('Не удалось изменить публичную ссылку', 'Не удалось изменить публичную ссылку'),
        ),
      );
    } finally {
      linkBusyRef.current = false;
      setLinkBusy(false);
    }
  }, [createPublicLink, id, refetch, t]);

  const handleRevokePublicLink = useCallback(async () => {
    if (!id || linkBusyRef.current) return;
    linkBusyRef.current = true;
    setLinkBusy(true);
    setPublicLinkError(null);
    try {
      await deletePublicLink({variables: {reportId: id}});
      await refetch();
    } catch (cause) {
      setPublicLinkError(
        apolloErrorMessage(
          cause,
          t('Не удалось изменить публичную ссылку', 'Не удалось изменить публичную ссылку'),
        ),
      );
    } finally {
      linkBusyRef.current = false;
      setLinkBusy(false);
    }
  }, [deletePublicLink, id, refetch, t]);

  const publicLink = useMemo(() => {
    const link = report?.publicLink;
    if (!link) return null;
    // Адрес собираем на месте: бэкенд знает только свой API-хост, а сборки
    // для adsensor.ru и adsensor.io живут на разных доменах.
    return {
      url: `${window.location.origin}/${ROUTES.SHARED_REPORT_BASE}/${link.slug}`,
      password: link.password ?? null,
    };
  }, [report?.publicLink]);

  if (!id) {
    return (
      <ReportUnavailable
        title={t(`Отчёт не${NBSP}выбран`, `Отчёт не${NBSP}выбран`)}
        detail={t(
          `В${NBSP}адресе страницы нет идентификатора${NBSP}отчёта.`,
          `В${NBSP}адресе страницы нет идентификатора${NBSP}отчёта.`,
        )}
      />
    );
  }

  if (loading) {
    return <ReportUnavailable title={t('Загружаем отчёт', 'Загружаем отчёт')} />;
  }

  if (error) {
    return (
      <ReportUnavailable
        title={t(`Не удалось загрузить${NBSP}отчёт`, `Не удалось загрузить${NBSP}отчёт`)}
        detail={t(
          `Обновите страницу или${NBSP}вернитесь в${NBSP}архив.`,
          `Обновите страницу или${NBSP}вернитесь в${NBSP}архив.`,
        )}
      />
    );
  }

  if (!report) {
    return (
      <ReportUnavailable
        title={t(`Отчёт не${NBSP}найден`, `Отчёт не${NBSP}найден`)}
        detail={t(
          `Возможно, ссылка устарела или${NBSP}отчёт принадлежит другому${NBSP}аккаунту.`,
          `Возможно, ссылка устарела или${NBSP}отчёт принадлежит другому${NBSP}аккаунту.`,
        )}
      />
    );
  }

  if (report.status === 'ERROR') {
    const reason = report.errorReason as ReportErrorReason | null;
    return (
      <ReportUnavailable
        title={STATUS_LABEL.ERROR}
        detail={reason ? ERROR_REASON_LABEL[reason] : null}
      />
    );
  }

  if (report.status !== 'READY') {
    return (
      <ReportUnavailable
        title={STATUS_LABEL[report.status]}
        detail={t(
          `Загляните позже${NBSP}— сборка ещё${NBSP}идёт.`,
          `Загляните позже${NBSP}— сборка ещё${NBSP}идёт.`,
        )}
      />
    );
  }

  // Схема этого не допускает, но экран обязан говорить правду: `READY` без
  // снимка — не «сборка идёт», а пустое содержимое у отчёта, который её уже
  // прошёл.
  if (!reportData) {
    return (
      <ReportUnavailable
        title={t(`Данные отчёта${NBSP}недоступны`, `Данные отчёта${NBSP}недоступны`)}
        detail={t(
          `Отчёт числится готовым, но${NBSP}его содержимое не пришло. Попробуйте открыть позже или${NBSP}вернитесь в${NBSP}архив.`,
          `Отчёт числится готовым, но${NBSP}его содержимое не пришло. Попробуйте открыть позже или${NBSP}вернитесь в${NBSP}архив.`,
        )}
      />
    );
  }

  return (
    <ExecutiveReport
      data={reportData}
      dataDate={toDataDate(report.generatedAt, report.periodEnd)}
      onLogoClick={handleLogoClick}
      publicLink={publicLink}
      onCreatePublicLink={handleCreatePublicLink}
      onRevokePublicLink={handleRevokePublicLink}
      publicLinkBusy={linkBusy}
      publicLinkError={publicLinkError}
    />
  );
};

export default Report;
